import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Platform, Pressable, Text, StyleSheet } from 'react-native';
import { useActiveAccountStore } from '../../stores/activeAccountStore';
import { EDITOR_COLORS, editorText, webControlStyle } from '../../styles/editorTheme';

export interface AccountFilterOption {
  accountId: string;
  dname: string;
  /** Original account id this account was migrated from, when it has one. */
  accountOrigId?: string;
}

interface AccountFilterSelectProps {
  /** Currently selected account id (a view-local draft — this control never switches the active account). */
  value: string | null;
  onChange: (accountId: string, accountName: string, accountOrigId: string) => void;
  /**
   * Fires whenever the selected option resolves or its AccountOrigId changes (including on mount and
   * when search results later supply the id), so a parent can sync to it without waiting for a pick.
   */
  onResolve?: (option: AccountFilterOption | null) => void;
  /** Append "(AccountId)" — or "(AccountId > AccountOrigId)" — after the account name. */
  showAccountId?: boolean;
  disabled?: boolean;
}

const optionText = (option: AccountFilterOption, showAccountId?: boolean): string => {
  if (!showAccountId) {
    return option.dname;
  }
  const ids = option.accountOrigId ? `${option.accountId} > ${option.accountOrigId}` : option.accountId;
  return `${option.dname} (${ids})`;
};

// Same registry ProGroupsSubmenuBar / ManageAccountsView use to share account search results.
const getRegistryOptions = (): AccountFilterOption[] => {
  const registry = (globalThis as any).__proGroupsAccountSelectorOptions;
  return Array.isArray(registry?.options) ? registry.options : [];
};

/**
 * Account dropdown for use as a filter inside a view. Options are the active account plus any
 * accounts surfaced by Account > Manage Accounts searches. Changing it only reports the pick to
 * the parent — it does not change the active account or load anything.
 */
export const AccountFilterSelect: React.FC<AccountFilterSelectProps> = ({ value, onChange, onResolve, showAccountId, disabled }) => {
  const activeAccountId = useActiveAccountStore((state) => state.activeAccountId);
  const activeAccountName = useActiveAccountStore((state) => state.activeAccountName);
  const [registryOptions, setRegistryOptions] = useState<AccountFilterOption[]>(getRegistryOptions);

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.addEventListener !== 'function') {
      return;
    }
    const refresh = () => setRegistryOptions([...getRegistryOptions()]);
    window.addEventListener('progroups:account-selector-options-updated', refresh);
    return () => window.removeEventListener('progroups:account-selector-options-updated', refresh);
  }, []);

  const options = useMemo(() => {
    const map = new Map<string, AccountFilterOption>();
    if (activeAccountId) {
      map.set(activeAccountId, { accountId: activeAccountId, dname: activeAccountName || `Account ${activeAccountId}` });
    }
    registryOptions.forEach((option) => map.set(option.accountId, option));
    return Array.from(map.values());
  }, [activeAccountId, activeAccountName, registryOptions]);

  const selectedOption = options.find((candidate) => candidate.accountId === value) ?? null;
  const resolveKey = selectedOption ? `${selectedOption.accountId}|${selectedOption.accountOrigId ?? ''}` : '';
  const onResolveRef = useRef(onResolve);
  onResolveRef.current = onResolve;
  useEffect(() => {
    onResolveRef.current?.(selectedOption);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resolveKey]);

  const handleChange = (accountId: string) => {
    const option = options.find((candidate) => candidate.accountId === accountId);
    onChange(accountId, option?.dname || `Account ${accountId}`, option?.accountOrigId ?? '');
  };

  const isDisabled = disabled || options.length === 0;

  if (Platform.OS === 'web') {
    return (
      <select
        style={{ ...webControlStyle, minWidth: 220 } as any}
        value={value || ''}
        onChange={(event: any) => handleChange(String(event?.target?.value ?? ''))}
        disabled={isDisabled}
        aria-label="Account"
      >
        {options.map((option) => (
          <option key={option.accountId} value={option.accountId}>{optionText(option, showAccountId)}</option>
        ))}
      </select>
    );
  }

  const cycle = () => {
    if (options.length === 0) {
      return;
    }
    const index = options.findIndex((option) => option.accountId === value);
    handleChange(options[(index + 1) % options.length].accountId);
  };

  return (
    <Pressable style={styles.nativeSelect} onPress={cycle} disabled={isDisabled} accessibilityRole="button" accessibilityLabel="Account">
      <Text style={styles.nativeSelectText}>
        {selectedOption ? optionText(selectedOption, showAccountId) : 'Select...'} ▼
      </Text>
    </Pressable>
  );
};

const styles = StyleSheet.create({
  nativeSelect: {
    minWidth: 220,
    height: 28,
    borderWidth: 1,
    borderColor: EDITOR_COLORS.controlBorder,
    borderRadius: 3,
    paddingHorizontal: 7,
    justifyContent: 'center',
  },
  nativeSelectText: {
    ...editorText,
    color: EDITOR_COLORS.textPrimary,
  },
});
